/**
 * Hook for managing typed field notes with geolocation.
 * 
 * Supports CRUD operations, automatic GPS tagging, and category filtering.
 */

import { useState, useCallback, useEffect } from 'react';
import * as Location from 'expo-location';
import { authApi, FieldNote, FieldNoteCategory } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

interface UseFieldNotesParams {
  portfolioId: number;
  opportunityId: number;
}

export function useFieldNotes({ portfolioId, opportunityId }: UseFieldNotesParams) {
  const [notes, setNotes] = useState<FieldNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (portfolioId && opportunityId) {
      fetchNotes();
    }
  }, [portfolioId, opportunityId]);

  const fetchNotes = useCallback(async () => {
    if (!portfolioId || !opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getFieldNotes(portfolioId, opportunityId);
      if (response.success) {
        setNotes(response.fieldNotes);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load field notes';
      logger.error('Failed to fetch field notes', err, 'field-notes');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  const getCurrentLocation = useCallback(async (): Promise<{ latitude: number; longitude: number } | null> => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') return null;
      const location = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
      return { latitude: location.coords.latitude, longitude: location.coords.longitude };
    } catch {
      return null;
    }
  }, []);

  const createNote = useCallback(async (data: {
    category?: FieldNoteCategory;
    title?: string;
    body: string;
    tagLocation?: boolean;
  }) => {
    setSaving(true);
    setError(null);
    try {
      let lat: number | null = null;
      let lng: number | null = null;
      if (data.tagLocation !== false) {
        const loc = await getCurrentLocation();
        lat = loc?.latitude ?? null;
        lng = loc?.longitude ?? null;
      }

      const response = await authApi.createFieldNote(portfolioId, opportunityId, {
        category: data.category || 'observation',
        title: data.title,
        body: data.body,
        latitude: lat,
        longitude: lng,
      });

      if (response.success) {
        setNotes(prev => [response.fieldNote, ...prev]);
        return response.fieldNote;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save note';
      logger.error('Failed to create field note', err, 'field-notes');
      setError(msg);
    } finally {
      setSaving(false);
    }
    return null;
  }, [portfolioId, opportunityId, getCurrentLocation]);

  const updateNote = useCallback(async (noteId: number, data: {
    category?: FieldNoteCategory; title?: string; body?: string;
  }) => {
    setError(null);
    try {
      const response = await authApi.updateFieldNote(portfolioId, opportunityId, noteId, data);
      if (response.success) {
        setNotes(prev => prev.map(n => n.id === noteId ? response.fieldNote : n));
        return response.fieldNote;
      }
    } catch (err) {
      logger.error('Failed to update field note', err, 'field-notes');
      setError('Failed to update note');
    }
    return null;
  }, [portfolioId, opportunityId]);

  const deleteNote = useCallback(async (noteId: number) => {
    setError(null);
    try {
      await authApi.deleteFieldNote(portfolioId, opportunityId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      logger.error('Failed to delete field note', err, 'field-notes');
      setError('Failed to delete note');
    }
  }, [portfolioId, opportunityId]);

  return {
    notes,
    loading,
    saving,
    error,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
  };
}
